import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Building2, ListChecks, Loader2, Send, Trash2, FlaskConical, ExternalLink, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { BriefCard } from '@/components/copilot/BriefCard';
import { Pill, MatchTypePill, CrmFlags, TIER_STYLE } from '@/components/copilot/badges';
import { ContactsList, ResearchPanel } from '@/components/copilot/ResearchPanel';

const API = '/api';
const AVAIL = ['Unknown', 'Confirmed', 'Historically partnered', 'Apparently unavailable', 'Needs verification'];
const OUTREACH = ['Not started', 'Planned', 'Contacted', 'Awaiting reply', 'In discussion', 'Declined', 'Parked'];

const ListIndex = () => {
  const [lists, setLists] = useState(null);
  useEffect(() => {
    axios.get(`${API}/copilot/opportunity-lists`, { withCredentials: true }).then(r => setLists(r.data || [])).catch(() => setLists([]));
  }, []);
  const del = async (id) => {
    if (!window.confirm('Delete this opportunity list?')) return;
    await axios.delete(`${API}/copilot/opportunity-lists/${id}`, { withCredentials: true });
    setLists(l => l.filter(x => x.id !== id));
  };
  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="opportunity-lists-page">
      <div className="flex items-center gap-2 mb-4">
        <ListChecks className="w-5 h-5 text-sky-600" />
        <h1 className="text-lg font-bold text-slate-900">Opportunity Lists</h1>
        <Link to="/copilot" className="ml-auto text-xs text-sky-700 hover:underline" data-testid="back-to-copilot">Research Copilot →</Link>
      </div>
      {lists === null && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      {lists?.length === 0 && <p className="text-sm text-slate-500">No lists yet. Run a discovery search in Research Copilot, shortlist companies and click <b>Create Opportunity List</b>.</p>}
      <div className="space-y-2">
        {(lists || []).map(l => (
          <div key={l.id} className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-3 hover:border-sky-300" data-testid={`list-card-${l.id}`}>
            <div className="flex-1 min-w-0">
              <Link to={`/opportunity-lists/${l.id}`} className="text-sm font-semibold text-slate-900 hover:text-sky-700">{l.name}</Link>
              <p className="text-xs text-slate-500 truncate">{l.requirement}</p>
              <p className="text-[10px] text-slate-400">{l.count} companies · {(l.created_at || '').slice(0, 10)} · {l.created_by_name}</p>
            </div>
            <button onClick={() => del(l.id)} className="p-1.5 text-slate-400 hover:text-rose-600 rounded hover:bg-rose-50" data-testid={`delete-list-${l.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const ItemCard = ({ it, listId, navigate, onPatch }) => {
  const [open, setOpen] = useState(false);
  const path = it.kind === 'pharma' ? `/company-profiles/${it.company_id}` : `/biotech/${it.company_id}`;
  const canNav = !!it.company_id;
  return (
    <div className={`rounded-lg border bg-white ${it.crm?.dnc ? 'border-rose-200' : 'border-slate-200'}`} data-testid={`list-item-${it.key}`}>
      <div className="p-3 flex flex-wrap gap-3">
        <div className="min-w-[200px] flex-1">
          <div className="flex items-center gap-2">
            {canNav ? <button onClick={() => navigate(path)} className="text-sm font-semibold text-slate-900 hover:text-sky-700 hover:underline">{it.company_name}</button> : <span className="text-sm font-semibold text-slate-900">{it.company_name}</span>}
            {it.tier && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${TIER_STYLE[it.tier] || ''}`}>{it.tier}</span>}
            <MatchTypePill value={it.match_type} />
            {it.origin === 'web' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border bg-emerald-600 text-white border-emerald-600">{it.company_id ? 'From web · added' : 'NEW — not in ForesightRx'}</span>}
          </div>
          <p className="text-[10px] text-slate-500">{it.country || '—'} · {it.development_stage || 'Stage not identified'} · Source: {it.source}</p>
          <div className="mt-1 text-xs text-slate-700">
            {(it.relevant_items || []).map((x, i) => <div key={i} className="flex items-center gap-1">{x.type === 'platform' ? <Building2 className="w-3 h-3 text-indigo-500" /> : <FlaskConical className="w-3 h-3 text-violet-500" />}{x.name}{x.stage ? <span className="text-slate-400"> · {x.stage}</span> : null}</div>)}
          </div>
          <p className="text-xs text-slate-700 mt-2"><span className="font-semibold">Why it matched:</span> {it.rationale}</p>
          {it.evidence && <p className="text-[11px] italic text-slate-500 mt-0.5">“{it.evidence}”</p>}
          {it.source_urls?.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{it.source_urls.map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-[9px] text-sky-700 hover:underline max-w-[180px] truncate"><ExternalLink className="w-2.5 h-2.5" />{u.replace(/^https?:\/\//, '')}</a>)}</div>}
        </div>
        <div className="w-full md:w-[260px] space-y-1.5 text-xs">
          <div className="flex items-center gap-2"><span className="w-24 text-slate-500">Relevance</span><Pill value={it.relevance} /></div>
          <div className="flex items-center gap-2"><span className="w-24 text-slate-500">Manufacturing</span><Pill value={it.manufacturing} /></div>
          <div className="flex items-center gap-2"><span className="w-24 text-slate-500">Availability</span>
            <select value={it.availability_status || it.partnering || 'Unknown'} onChange={e => onPatch(it.key, { availability_status: e.target.value })} className="text-[11px] border border-slate-200 rounded px-1 py-0.5 bg-white" data-testid={`availability-${it.key}`}>
              {AVAIL.map(a => <option key={a}>{a}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2"><span className="w-24 text-slate-500">Outreach</span>
            <select value={it.outreach_status || (it.crm?.contacted ? 'Contacted' : 'Not started')} onChange={e => onPatch(it.key, { outreach_status: e.target.value })} className="text-[11px] border border-slate-200 rounded px-1 py-0.5 bg-white" data-testid={`outreach-${it.key}`}>
              {OUTREACH.map(a => <option key={a}>{a}</option>)}
            </select>
          </div>
          <div className="flex items-start gap-2"><span className="w-24 text-slate-500 shrink-0">CRM</span><CrmFlags crm={it.crm} /></div>
        </div>
      </div>
      <div className="px-3 pb-3 flex flex-wrap items-center gap-1.5">
        {canNav && <button onClick={() => navigate(path)} className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-slate-200 bg-white hover:bg-slate-50"><Building2 className="w-3 h-3" /> View Company</button>}
        {canNav && !it.crm?.dnc && <button onClick={() => navigate(`${path}#outreach`)} className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-slate-900 bg-slate-900 text-white hover:bg-slate-800" data-testid={`outreach-btn-${it.key}`}><Send className="w-3 h-3" /> Start Outreach</button>}
        <button onClick={() => setOpen(o => !o)} className="ml-auto text-[10px] text-slate-500 hover:text-slate-800" data-testid={`toggle-item-${it.key}`}>{open ? 'Hide' : 'Show'} contacts & research ({(it.contacts || []).length} contacts)</button>
      </div>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          <div className="rounded-md border border-slate-200 p-2"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">BD contacts on file</p><ContactsList contacts={it.contacts} /></div>
          <ResearchPanel research={it.research} />
        </div>
      )}
    </div>
  );
};

const ListDetail = ({ id }) => {
  const [list, setList] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    axios.get(`${API}/copilot/opportunity-lists/${id}`, { withCredentials: true }).then(r => setList(r.data)).catch(() => toast.error('List not found'));
  }, [id]);
  const patch = async (key, body) => {
    try {
      await axios.patch(`${API}/copilot/opportunity-lists/${id}/items/${encodeURIComponent(key)}`, body, { withCredentials: true });
      setList(l => ({ ...l, items: l.items.map(it => it.key === key ? { ...it, ...body } : it) }));
    } catch { toast.error('Update failed'); }
  };
  const download = async () => {
    try {
      const r = await axios.get(`${API}/copilot/opportunity-lists/${id}/export.xlsx`, { responseType: 'blob', withCredentials: true });
      const name = (r.headers['content-disposition'] || '').match(/filename="?([^";]+)"?/)?.[1] || 'opportunity_list.xlsx';
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      let detail = e?.response?.status ? `HTTP ${e.response.status}` : (e?.message || 'network error');
      try { const t = await e.response.data.text(); detail = JSON.parse(t).detail || detail; } catch { /* not json */ }
      toast.error(`Download failed — ${detail}`);
    }
  };
  if (!list) return <div className="p-6"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>;
  const s = { n: list.items.length, product: list.items.filter(i => ['Product', 'Both'].includes(i.match_type)).length, platform: list.items.filter(i => ['Platform', 'Both'].includes(i.match_type)).length, mfg: list.items.filter(i => i.manufacturing === 'Confirmed').length };
  return (
    <div className="p-6 max-w-6xl mx-auto space-y-3" data-testid="opportunity-list-detail">
      <div className="flex items-center gap-2">
        <Link to="/opportunity-lists" className="p-1 rounded hover:bg-slate-100 text-slate-500" data-testid="back-to-lists"><ArrowLeft className="w-4 h-4" /></Link>
        <h1 className="text-lg font-bold text-slate-900">{list.name}</h1>
        <span className="text-[10px] text-slate-400 ml-2">{(list.created_at || '').slice(0, 10)} · {list.created_by_name}</span>
        <Button size="sm" className="ml-auto text-xs bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={download} data-testid="download-list-btn"><Download className="w-3.5 h-3.5" /> Download Excel (companies + emails)</Button>
        <Button size="sm" variant="outline" className="text-xs" onClick={() => navigate('/copilot')} data-testid="continue-in-copilot">Continue in Copilot</Button>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Original client requirement</p>
        <p className="text-sm text-slate-800 whitespace-pre-wrap" data-testid="list-requirement">{list.requirement}</p>
      </div>
      <BriefCard brief={list.brief} compact />
      <p className="text-xs font-semibold text-slate-900" data-testid="list-summary">{s.n} companies · {s.product} product opportunities · {s.platform} platform opportunities · {s.mfg} with confirmed manufacturing</p>
      <div className="space-y-2">
        {list.items.map(it => <ItemCard key={it.key} it={it} listId={id} navigate={navigate} onPatch={patch} />)}
      </div>
    </div>
  );
};

export default function OpportunityListsPage() {
  const { id } = useParams();
  return id ? <ListDetail id={id} /> : <ListIndex />;
}
